import { useQuestionBuilder } from 'lib-node-question-builder';
import { and, formIs, screenIs } from 'lib-node-question-builder/utils';
import { useMemo } from 'react';

import { UNDERWRITING_METHODS } from '~/common/constants/api-ids';
import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import useIsWriteAccessDisabled from '~/common/hooks/useIsWriteAccessDisabled';
import Divider from '~/common/mui/Divider';
import Loan1003FormContainer from '~/pages/Loan1003DataGrids/common/components/Loan1003FormContainer';
import Loan1003FormSection from '~/pages/Loan1003DataGrids/common/components/Loan1003FormSection';
import { READONLY_CONDITION } from '~/pages/Loan1003DataGrids/common/utils/utils';

import { useBorrower1003Store } from '../../../borrower1003Store';
import {
  useBorrowerDeclarationsFormData,
  useBorrowerDeclarationsMutation,
} from './hooks';

type DeclarationsFormProps = {
  borrowerId: number;
};

const YES_NO_OPTIONS = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
];

export function DeclarationsForm(props: Readonly<DeclarationsFormProps>) {
  const isWriteAccessDisabled = useIsWriteAccessDisabled();
  const isRefi = useGetLoanSummary().data?.propertyData?.isRefi;

  const { data, isLoading } = useBorrowerDeclarationsFormData(props.borrowerId);

  const { mutate, isLoading: isMutating } = useBorrowerDeclarationsMutation();

  const screenData = useMemo(() => {
    return {
      ...data?.screenData,
      isWriteAccessDisabled: isWriteAccessDisabled,
    };
  }, [data?.screenData, isWriteAccessDisabled]);

  const { form, handleSubmit } = useQuestionBuilder({
    formData: data?.formData,
    screenData,
    listeners: {
      onChange: ({ fieldApi, formApi }) => {
        if (
          fieldApi.name === 'isPrimaryResidence' ||
          fieldApi.name === 'ownershipInterestInLastThreeYears'
        ) {
          const isPrimaryResidence =
            formApi.state.values.isPrimaryResidence === 'Yes';

          const ownershipInterestInLastThreeYears =
            formApi.state.values.ownershipInterestInLastThreeYears === 'Yes';

          formApi
            .getFieldInfo('firstTimeHomeBuyer')
            .instance?.setValue(
              isPrimaryResidence && !ownershipInterestInLastThreeYears
                ? 'Yes'
                : 'No',
            );
        }
      },
    },
    hooks: {
      onSubmit: (values) => {
        mutate(values);
      },
    },
  });

  return (
    <form.Provider>
      <Loan1003FormContainer
        isLoading={isLoading || isMutating}
        readOnlyMode={isWriteAccessDisabled}
        onCloseClicked={() => {
          useBorrower1003Store.setState({ selectedBorrowerId: null });
        }}
        onSaveClicked={() => {
          handleSubmit();
        }}
      >
        <Loan1003FormSection title="Declarations">
          <form.Radio
            name="isPrimaryResidence"
            label="A1. Will you occupy the property as your primary residence?"
            rules={{ required: true }}
            hasBottomBorder={false}
            conditions={{ isDisabled: READONLY_CONDITION }}
            options={[
              { label: 'Yes', value: 'Yes' },
              { label: 'No', value: 'No' },
            ]}
          />
          <form.Radio
            name="ownershipInterestInLastThreeYears"
            label="A2. Have you had an ownership interest in another property in the last three years?"
            rules={{ required: true }}
            hasBottomBorder={false}
            options={YES_NO_OPTIONS}
            conditions={{
              isDisabled: READONLY_CONDITION,
              isVisible: formIs('isPrimaryResidence', 'Yes'),
            }}
          />
          <form.Select
            name="ownershipPropertyType"
            label="What type of property did you own?"
            rules={{ required: true }}
            placeholder="Select a property type"
            options={data?.dropdownOptions?.ownershipPropertyType ?? []}
            hasBottomBorder={false}
            conditions={{
              isDisabled: READONLY_CONDITION,
              isVisible: and(
                formIs('isPrimaryResidence', 'Yes'),
                formIs('ownershipInterestInLastThreeYears', 'Yes'),
              ),
            }}
          />
          <form.Select
            name="titleType"
            label="How did you hold title to the property?"
            rules={{ required: true }}
            placeholder="Select how the title was held"
            options={data?.dropdownOptions?.titleType ?? []}
            hasBottomBorder={false}
            conditions={{
              isDisabled: READONLY_CONDITION,
              isVisible: and(
                formIs('isPrimaryResidence', 'Yes'),
                formIs('ownershipInterestInLastThreeYears', 'Yes'),
              ),
            }}
          />
          <Divider sx={{ opacity: 0.5 }} />
          <form.Radio
            name="isFamilyRelationOrBusinessAffiliation"
            label="B: If this is a Purchase Transaction: Do you have a family relationship or business affiliation with the seller of the property?"
            rules={{ required: true }}
            conditions={{ isDisabled: READONLY_CONDITION }}
            options={YES_NO_OPTIONS}
          />
          <form.Radio
            name="anyUndisclosedMoneyBeingBorrowed"
            label="C1. Are you borrowing any money for this real estate transaction (e.g., money for your closing costs or down payment) or obtaining any money from another party, such as the seller or realtor, that you have not disclosed on this loan application?"
            rules={{ required: true }}
            hasBottomBorder={false}
            conditions={{ isDisabled: READONLY_CONDITION }}
            options={YES_NO_OPTIONS}
          />
          <form.Currency
            name="amountOfUndisclosedMoney"
            label="C2. What is the amount of this money?"
            rules={{
              required: true,
              onSubmit: {
                type: 'number',
                gt: 0,
                message: 'Amount must be greater than 0',
              },
            }}
            hasBottomBorder={false}
            conditions={{
              isVisible: formIs('anyUndisclosedMoneyBeingBorrowed', 'Yes'),
              isDisabled: READONLY_CONDITION,
            }}
          />
          <form.Radio
            name="applyingForAMortgageOnAnotherProperty"
            label="D1. Have you or will you be applying for a mortgage loan on another property (not the property securing this loan) on or before closing this transaction that is not disclosed on this loan application?"
            rules={{ required: true }}
            hasBottomBorder={false}
            conditions={{ isDisabled: READONLY_CONDITION }}
            options={YES_NO_OPTIONS}
          />
          <form.Radio
            name="applyingForNewCreditCard"
            label="D2. Have you or will you be applying for any new credit (e.g., installment loan, credit card, etc.) on or before closing this loan that is not disclosed on this application?"
            rules={{ required: true }}
            conditions={{ isDisabled: READONLY_CONDITION }}
            options={YES_NO_OPTIONS}
          />
          <Divider sx={{ opacity: 0.5 }} />
          <form.Radio
            name="isPropertySubjectToAlien"
            label="E. Will this property be subject to a lien that could take priority over the first mortgage lien, such as a clean energy lien paid through your property taxes (e.g., the Property Assessed Clean Energy Program)?"
            rules={{ required: true }}
            conditions={{ isDisabled: READONLY_CONDITION }}
            options={YES_NO_OPTIONS}
          />
          <form.Radio
            name="isCosignerOnOtherDebt"
            label="F. Are you a co-signer or guarantor on any debt or loan that is not disclosed on this application?"
            rules={{ required: true }}
            conditions={{ isDisabled: READONLY_CONDITION }}
            options={YES_NO_OPTIONS}
          />
          <form.Radio
            name="anyOutstandingJudgements"
            label="G. Are there any outstanding judgments against you?"
            rules={{ required: true }}
            conditions={{ isDisabled: READONLY_CONDITION }}
            options={YES_NO_OPTIONS}
          />
          <form.Radio
            name="isDelinquentOrDefaultOnFederalDebt"
            label="H. Are you currently delinquent or in default on a Federal debt?"
            rules={{ required: true }}
            conditions={{ isDisabled: READONLY_CONDITION }}
            options={YES_NO_OPTIONS}
          />
          <form.Radio
            name="anyPersonalFinancialLiabilityInLawsuit"
            label="I. Are you a party to a lawsuit in which you potentially have any personal financial liability?"
            rules={{ required: true }}
            conditions={{ isDisabled: READONLY_CONDITION }}
            options={YES_NO_OPTIONS}
          />
          <form.Radio
            name="anyConveyanceOnPropertyInLieuOfForeclosure"
            label="J. Have you conveyed title to any property in lieu of foreclosure in the past 7 years?"
            rules={{ required: true }}
            conditions={{ isDisabled: READONLY_CONDITION }}
            options={YES_NO_OPTIONS}
          />
          <form.Radio
            name="anyPropertyPreForeclosureInLastSevenYears"
            label="K. Within the past 7 years, have you completed a pre-foreclosure sale or short sale, whereby the property was sold to a third party and the Lender agreed to accept less than the outstanding mortgage balance due?"
            rules={{ required: true }}
            conditions={{ isDisabled: READONLY_CONDITION }}
            options={YES_NO_OPTIONS}
          />
          <form.Radio
            name="anyPropertyForeclosedInLastSevenYears"
            label="L. Have you had property foreclosed upon in the last 7 years?"
            rules={{ required: true }}
            conditions={{ isDisabled: READONLY_CONDITION }}
            options={YES_NO_OPTIONS}
          />
          <form.Radio
            name="declaredBankruptcyInLastSevenYears"
            label="M. Have you declared bankruptcy within the past 7 years?"
            hasBottomBorder={false}
            rules={{ required: true }}
            conditions={{
              isDisabled: READONLY_CONDITION,
            }}
            options={YES_NO_OPTIONS}
          />
          <form.CheckboxGroup
            name="bankruptciesInLastSevenYears"
            label="Indicate the type(s) of bankruptcies declared in the past 7 years:"
            hasBottomBorder={false}
            conditions={{
              isVisible: formIs('declaredBankruptcyInLastSevenYears', 'Yes'),
              isDisabled: READONLY_CONDITION,
            }}
            options={[
              { label: 'Chapter 7', value: 'Chapter 7' },
              { label: 'Chapter 11', value: 'Chapter 11' },
              { label: 'Chapter 12', value: 'Chapter 12' },
              { label: 'Chapter 13', value: 'Chapter 13' },
            ]}
          />
          <Divider sx={{ opacity: 0.5 }} />
          <form.Radio
            name="firstTimeHomeBuyer"
            label="N. Are any of the occupant borrowers first time homebuyers?"
            options={YES_NO_OPTIONS}
            conditions={{
              isDisabled: isWriteAccessDisabled || !isRefi,
            }}
          />
          <form.Radio
            name="hudApprovedCounselingFlag"
            label="O. HUD Approved Counseling"
            rules={{ required: true }}
            options={YES_NO_OPTIONS}
            conditions={{
              isDisabled: READONLY_CONDITION,
            }}
          />
          <form.Select
            name="duExplanationForeclosure"
            label="DU Explanation - Foreclosure"
            conditions={{
              isVisible: screenIs(
                'underwritingMethod',
                UNDERWRITING_METHODS.DesktopUnderwriter,
              ),
              isDisabled: READONLY_CONDITION,
            }}
            hasBottomBorder={false}
            options={data?.dropdownOptions?.duExplanationForeclosure ?? []}
          />
          <form.Select
            name="duExplanationDelinquency"
            label="DU Explanation - Delinquency"
            conditions={{
              isVisible: screenIs(
                'underwritingMethod',
                UNDERWRITING_METHODS.DesktopUnderwriter,
              ),
              isDisabled: READONLY_CONDITION,
            }}
            hasBottomBorder={false}
            options={data?.dropdownOptions?.duExplanationDelinquency ?? []}
          />
          <form.Select
            name="duExplanationBankruptcy"
            label="DU Explanation - Bankruptcy"
            conditions={{
              isVisible: screenIs(
                'underwritingMethod',
                UNDERWRITING_METHODS.DesktopUnderwriter,
              ),
              isDisabled: READONLY_CONDITION,
            }}
            hasBottomBorder={false}
            options={data?.dropdownOptions?.duExplanationBankruptcy ?? []}
          />
        </Loan1003FormSection>
      </Loan1003FormContainer>
    </form.Provider>
  );
}
